import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';
import { engineContext, isFormType, useInspectionForm, useSaveCustomFields, type FormType, type InspectionForm } from '../../../api/forms';
import { useInspection } from '../../../api/inspections';
import { useEnumLabel } from '../../../api/enums';
import { isApiError } from '../../../api/errors';
import { Breadcrumbs, ErrorSummary, InsetText } from '../../../components/idsk';
import { useNavigateWithToast } from '../../../components/common/navigateWithToast';
import { PageHeading } from '../../../layout/PageHeading';
import { usePageTitle } from '../../../layout/usePageTitle';
import { flatten, type FieldDef, type FieldType } from '../../../form-engine';
import { mergeDefinition } from '../../../form-engine';
import { text } from '../../../form-engine/types';
import { NotFoundPage } from '../../common/NotFoundPage';
import { ForbiddenPage } from '../../common/ForbiddenPage';
import { LoadingPage } from '../../common/LoadingPage';
import { QueryError } from '../../common/QueryError';
import { FieldEditor } from '../../forms/builder/FieldEditor';
import { FieldActions, FieldLinks } from '../../forms/builder/FieldActions';
import { FormCanvas, canvasEditId } from '../../forms/builder/FormCanvas';
import { ControlPalette, usePaletteTargets } from '../../forms/builder/ControlPalette';
import { FieldMovePage } from '../../forms/builder/FieldMovePage';
import { PalettePage } from '../../forms/builder/PalettePage';
import { locate, tableLock } from '../../forms/builder/definitionOps';
import { isGroup, paletteQuery, positionParam, type PaletteItem } from '../../forms/builder/palette';

/** The team's own sections and repeating groups are targets of the palette (template fields are not). */
const own = (f: FieldDef) => f.origin === 'INSPECTION';

function newCustomFieldPath(inspectionId: string, formType: FormType, item: PaletteItem, parent: string | null, index?: number): string {
  return `/inspekcie/${inspectionId}/formulare/${formType}/polia/novy/${item.type}${paletteQuery(item, parent, index)}`;
}

function useFormParams() {
  const { id, type, key, fieldType } = useParams();
  return { id: id!, formType: isFormType(type) ? type : undefined, key, fieldType };
}

/** Loads inspection + form and only lets through the editable (current status) form. */
function CustomShell({ children }: { children: (ctx: { inspectionId: string; formType: FormType; form: InspectionForm; title: string; number: string }) => React.ReactNode }) {
  const { id, formType } = useFormParams();
  const label = useEnumLabel();
  const inspection = useInspection(id);
  const form = useInspectionForm(id, formType);
  if (!formType) return <NotFoundPage />;
  if (inspection.error) return <QueryError error={inspection.error} asPage />;
  if (form.error) return <QueryError error={form.error} asPage />;
  if (inspection.isPending || form.isPending) return <LoadingPage />;
  if (!form.data.editable) return <ForbiddenPage />;
  return <>{children({ inspectionId: id, formType, form: form.data, title: label('formTypes', formType), number: inspection.data.number })}</>;
}

export function CustomFieldsPage() {
  return <CustomShell>{(ctx) => <CustomFields {...ctx} />}</CustomShell>;
}

function CustomFields({ inspectionId, formType, form, title, number }: { inspectionId: string; formType: FormType; form: InspectionForm; title: string; number: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage === 'en' ? 'en' : 'sk';
  const { key, fieldType } = useFormParams();
  const [params] = useSearchParams();
  const save = useSaveCustomFields(inspectionId, formType);
  const navigateWithToast = useNavigateWithToast();
  const [error, setError] = useState<string[]>([]);
  const [target, setTarget] = useState<string | null>(null);
  // the live preview of the open field
  const [preview, setPreview] = useState<FieldDef | null>(null);
  const targets = usePaletteTargets(form.customFields, own);
  const heading = t('customFields.title', { form: title });
  // the field whose properties are open: one of the team's fields (…/polia/:key) or a new one (…/polia/novy/:fieldType)
  const existing: FieldDef | null = key ? (locate(form.customFields, key)?.field ?? null) : null;
  const parentKey = !key ? params.get('skupina') : null;
  const parent = parentKey ? locate(form.customFields, parentKey)?.field : null;
  // a new field goes to the effective top level (after a template or custom field) or into one of the team's groups
  const siblings = parent ? (parent.children ?? []) : mergeDefinition(form.templateDefinition, form.customFields).fields;
  const position = Math.min(positionParam(params) ?? siblings.length, siblings.length);
  const editedType = (existing?.type ?? (!key ? fieldType : undefined)) as FieldType | undefined;
  const selection = editedType ? (existing?.key ?? `new-${editedType}-${parentKey ?? ''}-${position}`) : null;
  // another field opened: the preview of the previous one is dropped at once (state adjusted during render)
  const [previewOf, setPreviewOf] = useState(selection);
  if (previewOf !== selection) {
    setPreviewOf(selection);
    setPreview(null);
  }
  const editTitle = existing ? t('builder.editor.editTitle', { label: text(existing.label, lang) }) : editedType ? t('builder.editor.newTitle', { type: t(`builder.types.${editedType}.name`) }) : null;
  usePageTitle([editTitle, heading, number]);
  if ((key && !existing) || parent === undefined || (parent && !isGroup(parent))) return <NotFoundPage />;
  const base = `/inspekcie/${inspectionId}/formulare/${formType}`;
  const fieldsBase = `${base}/polia`;
  // keys with stored values are locked (immutable once a value exists)
  const locked = new Set(Object.keys(form.values));
  const lockedOptions = new Set(
    flatten(form.customFields)
      .filter((f) => locked.has(f.key))
      .flatMap((f) => [...(f.options ?? []).map((o) => o.value), ...(f.table?.columns ?? []).map((c) => tableLock('column', c.key)), ...(f.table?.rows ?? []).map((r) => tableLock('row', r.key))]),
  );

  /** Moves and removals are saved at once; false when the fields were not saved (the error summary says why). */
  const persist = async (next: { customFields: FieldDef[] }) => {
    setError([]);
    try {
      await save.mutateAsync({ data: next.customFields, version: form.version! });
      return true;
    } catch (e) {
      setError(isApiError(e) && e.fieldErrors.length ? e.fieldErrors.map((fe) => fe.message) : [isApiError(e) ? e.message : t('errors.unexpected')]);
      return false;
    }
  };

  return (
    <>
      <Breadcrumbs items={[{ text: t('inspections.title'), to: '/inspekcie' }, { text: number, to: `/inspekcie/${inspectionId}` }, { text: title, to: base }, { text: t('customFields.crumb') }]} />
      {error.length > 0 && <ErrorSummary errors={error.map((text) => ({ text }))} />}
      <PageHeading caption={number}>{heading}</PageHeading>
      <InsetText>{t('customFields.intro')}</InsetText>
      <div className="govuk-grid-row app-builder-layout">
        <div className="govuk-grid-column-two-thirds-from-desktop">
          <FormCanvas
            mode="custom"
            definition={form.templateDefinition}
            customFields={form.customFields}
            context={engineContext(form)}
            selectPath={(k) => `${fieldsBase}/${k}`}
            newPath={(p) => `${fieldsBase}/novy${p ? `?skupina=${p}` : ''}`}
            selected={existing?.key ?? null}
            preview={preview && previewOf === selection ? { field: preview, position: existing ? undefined : { parentKey, index: position } } : null}
            dropPath={(item, p, index) => newCustomFieldPath(inspectionId, formType, item, p, index)}
            onChange={persist}
          />
        </div>
        <div className="govuk-grid-column-one-third-from-desktop">
          {editedType ? (
            <FieldEditor
              key={selection!}
              mode="custom"
              type={editedType}
              definition={form.templateDefinition}
              customFields={form.customFields}
              existing={existing}
              parentKey={parentKey}
              position={existing ? null : position}
              initialConstraints={params.get('zobrazenie') === 'select' ? { display: 'select' } : undefined}
              lockedKeys={locked}
              lockedOptions={lockedOptions}
              context={engineContext(form)}
              backTo={fieldsBase}
              backFocus={existing ? canvasEditId(existing.key) : 'add-field'}
              onPreview={setPreview}
              links={
                existing && (
                  <FieldLinks
                    mode="custom"
                    definition={form.templateDefinition}
                    customFields={form.customFields}
                    fieldKey={existing.key}
                    newPath={(p) => `${fieldsBase}/novy?skupina=${p}`}
                    movePath={(k) => `${fieldsBase}/${k}/presunut`}
                  />
                )
              }
              actions={
                existing && (
                  <FieldActions
                    mode="custom"
                    definition={form.templateDefinition}
                    customFields={form.customFields}
                    fieldKey={existing.key}
                    onChange={persist}
                    onRemoved={(message) => navigateWithToast(fieldsBase, message, { focus: 'add-field' })}
                  />
                )
              }
              onSave={async ({ customFields, field }) => {
                await save.mutateAsync({ data: customFields, version: form.version! });
                navigateWithToast(fieldsBase, t('builder.editor.saved', { label: text(field.label, lang) }), { focus: canvasEditId(field.key) });
              }}
            />
          ) : (
            <ControlPalette
              targets={targets}
              target={targets.some((x) => x.key === target) ? target : null}
              onTargetChange={setTarget}
              itemPath={(item, p) => newCustomFieldPath(inspectionId, formType, item, p)}
              draggable
            />
          )}
        </div>
      </div>
    </>
  );
}

export function CustomFieldTypePage() {
  return <CustomShell>{(ctx) => <CustomPalette {...ctx} />}</CustomShell>;
}

function CustomPalette({ inspectionId, formType, form, title, number }: { inspectionId: string; formType: FormType; form: InspectionForm; title: string; number: string }) {
  const targets = usePaletteTargets(form.customFields, own);
  return (
    <PalettePage
      backTo={`/inspekcie/${inspectionId}/formulare/${formType}/polia`}
      titleParts={[title, number]}
      targets={targets}
      itemPath={(item, parent) => newCustomFieldPath(inspectionId, formType, item, parent)}
    />
  );
}

export function CustomFieldMovePage() {
  return <CustomShell>{(ctx) => <CustomFieldMove {...ctx} />}</CustomShell>;
}

function CustomFieldMove({ inspectionId, formType, form, title, number }: { inspectionId: string; formType: FormType; form: InspectionForm; title: string; number: string }) {
  const { key } = useFormParams();
  const save = useSaveCustomFields(inspectionId, formType);
  const navigateWithToast = useNavigateWithToast();
  if (!key || !locate(form.customFields, key)) return <NotFoundPage />;
  const base = `/inspekcie/${inspectionId}/formulare/${formType}/polia`;
  return (
    <FieldMovePage
      mode="custom"
      definition={form.templateDefinition}
      customFields={form.customFields}
      fieldKey={key}
      backTo={base}
      titleParts={[title, number]}
      onSave={async ({ customFields, message }) => {
        await save.mutateAsync({ data: customFields, version: form.version! });
        navigateWithToast(base, message);
      }}
    />
  );
}
